import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useAuth, useClerk, useUser } from '@clerk/react';
import { format } from 'date-fns';
import { Mail, User, Calendar, Paperclip, Search, ChevronDown, ChevronLeft, ChevronRight, BarChart3, LogOut, Sparkles } from 'lucide-react';
import './App.css';
import EscalationReport from './components/EscalationReport';
import SLAMonitoringDashboard from './components/SLAMonitoringDashboard';
import AIDraftPanel from './components/AIDraftPanel';
import AIChatPanel from './components/AIChatPanel';
import Login from './components/Login';
import api from './utils/api';
import {
  User as UserType,
  Email,
  Thread,
  Employee,
  PaginationState,
  ApiResponse,
  PaginationResponse,
} from './types';

type ViewType = 'aichat' | 'inbox' | 'escalation' | 'sla';

function App() {
  const { isSignedIn, userId } = useAuth();
  const { signOut } = useClerk();
  const { user: clerkUser, isLoaded: isUserLoaded } = useUser();

  const [emails, setEmails] = useState<Email[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentView, setCurrentView] = useState<ViewType>('aichat');
  const [tabLoading, setTabLoading] = useState<boolean>(false);
  const [aiDraftThread, setAiDraftThread] = useState<Thread | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    totalEmails: 0,
    hasMore: false,
    limit: 50
  });

  // Employee filtering state
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [pendingFilter, setPendingFilter] = useState<string>('employee'); // 'employee' | 'client' | ''
  const [filterLoading, setFilterLoading] = useState<boolean>(false);

  // Check for existing authentication on app load (password login path)
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        const parsedUser: UserType = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Clerk login path: when Clerk session is established, exchange for a local JWT
  useEffect(() => {
    if (!isSignedIn || !userId || !isUserLoaded || isAuthenticated) return;

    const exchangeClerkSession = async () => {
      try {
        const email = clerkUser?.primaryEmailAddress?.emailAddress;

        if (!email) {
          console.error('Clerk: could not resolve primary email');
          return;
        }

        // Use clerk-signup for all Clerk sessions — it handles both new and existing users
        const response = await api.post('/auth/clerk-signup', { clerkUserId: userId, email });
        const { data } = response.data;

        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        setIsAuthenticated(true);
      } catch (error: any) {
        console.error('Clerk login exchange failed:', error?.response?.data?.error || error.message);
        // Backend rejected — sign out of Clerk so user isn't stuck
        try {
          await signOut();
        } catch (_) { /* ignore signOut errors */ }
      }
    };

    exchangeClerkSession();
  }, [isSignedIn, userId, isUserLoaded, clerkUser, isAuthenticated, signOut]);

  // Handle logout — clears local state and Clerk session if active
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setEmails([]);
    setSelectedThread(null);
    if (isSignedIn) signOut();
  };

  // Fetch emails from the backend with pagination
  const fetchEmails = useCallback(async (page: number = 1, reset: boolean = false) => {
    try {
      setLoading(true);
      const offset = (page - 1) * pagination.limit;

      const response = await api.get<ApiResponse<Email[]>>('/emails', {
        params: {
          limit: pagination.limit,
          offset,
          sortBy: 'emailDate',
          sortOrder: 'DESC',
          ...(selectedEmployeeId && { employeeId: selectedEmployeeId }),
          ...(pendingFilter && { pendingWith: pendingFilter })
        }
      });

      if (response.data.success && response.data.data) {
        if (reset || page === 1) {
          setEmails(response.data.data);
        } else {
          setEmails(prev => [...prev, ...response.data.data]);
        }

        // Update pagination info
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            currentPage: page,
            totalPages: response.data.pagination!.totalPages,
            totalEmails: response.data.pagination!.total,
            hasMore: response.data.pagination!.hasMore
          }));
        }
      } else {
        if (reset || page === 1) {
          setEmails([]);
        }
        console.error('Invalid response format:', response.data);
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
      if (reset || page === 1) {
        setEmails([]);
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, selectedEmployeeId, pendingFilter]);

  // Mark email as read
  const markAsRead = async (emailId: string) => {
    try {
      await api.put(`/emails/${emailId}/read`);
      setEmails(emails.map(email =>
        email._id === emailId ? { ...email, isRead: true } : email
      ));
    } catch (error) {
      console.error('Error marking email as read:', error);
    }
  };

  // Fetch employees for dropdown + auto-select for employee role
  const fetchEmployees = async () => {
    try {
      const response = await api.get<ApiResponse<Employee[]>>('/employees');
      if (response.data.success) {
        const allEmployees = response.data.data;
        setEmployees(allEmployees);

        // If user role is 'employee', auto-select their employee record
        if (user?.role === 'employee' && user?.email) {
          const matchedEmployee = allEmployees.find(
            (emp: Employee) => emp.email?.toLowerCase() === user.email?.toLowerCase()
              || emp.email?.toLowerCase().includes(user.email?.split('@')[0]?.toLowerCase() || '')
          );
          if (matchedEmployee) {
            setSelectedEmployeeId(String(matchedEmployee.id));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Handle employee selection
  const handleEmployeeChange = async (employeeId: string) => {
    setFilterLoading(true);
    setSelectedEmployeeId(employeeId);

    // Reset pagination
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalPages: 1,
      totalEmails: 0,
      hasMore: false
    }));

    // Clear current emails
    setEmails([]);

    // Fetch emails for selected employee (respecting search query)
    try {
      if (debouncedSearchQuery.trim()) {
        await searchEmails(debouncedSearchQuery, 1);
      } else {
        await fetchEmails(1, true);
      }
    } catch (error) {
      console.error('Error refreshing emails for employee:', error);
    } finally {
      setFilterLoading(false);
    }
  };

  // Load more emails (pagination)
  const loadMoreEmails = () => {
    if (pagination.hasMore && !loading) {
      fetchEmails(pagination.currentPage + 1, false);
    }
  };

  // Handle thread selection
  const handleThreadSelect = (thread: Thread) => {
    if (selectedThread?.threadId === thread.threadId) {
      setSelectedThread(null); // Collapse if already selected
    } else {
      setSelectedThread(thread);

      // Mark all emails in thread as read
      thread.emails.forEach(email => {
        if (!email.isRead) {
          markAsRead(email._id);
        }
      });
    }
  };

  // Authenticate with Gmail using stored tokens
  const authenticateGmail = async () => {
    try {
      setLoading(true);
      await api.post('/gmail/auth');
      setIsAuthenticated(true);
      // Only fetch emails if we're on the inbox view
      if (currentView === 'inbox') {
        fetchEmails();
      }
    } catch (error) {
      console.error('Authentication error:', error);
      alert('Failed to authenticate with Gmail. Please ensure tokens are set in gmail-tokens.json');
    } finally {
      setLoading(false);
    }
  };

  // Start OAuth2 flow for Gmail
  const startOAuthFlow = async () => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse<{ authUrl: string }>>('/gmail/auth-url');

      if (response.data.success) {
        const authUrl = response.data.data.authUrl;
        console.log('Opening OAuth2 authorization URL:', authUrl);

        // Open the authorization URL in a new window/tab
        window.open(authUrl, '_blank', 'width=600,height=700');

        // Show instructions to the user
        alert('OAuth2 authorization window opened. Please complete the authorization and then return here to continue.');
      } else {
        throw new Error('Failed to generate authorization URL');
      }
    } catch (error) {
      console.error('OAuth2 flow error:', error);
      alert('Failed to start OAuth2 flow: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Check OAuth2 callback status
  const checkOAuthStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse<{ authenticated: boolean }>>('/gmail/status');

      if (response.data.success && response.data.data.authenticated) {
        setIsAuthenticated(true);
        // Only fetch emails if we're on the inbox view
        if (currentView === 'inbox') {
          fetchEmails();
        }
        alert('Gmail authentication successful! You can now close the authorization window.');
      } else {
        alert('Authentication not yet complete. Please complete the authorization in the other window.');
      }
    } catch (error) {
      console.error('OAuth2 status check error:', error);
      alert('Failed to check authentication status: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };



  // Format email date
  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid date';
    }
  };

  // Extract sender name from email
  const getSenderName = (from: string): string => {
    const match = from.match(/"([^"]+)"/);
    return match ? match[1] : from.split('@')[0];
  };

  // Extract domain from email
  const getDomain = (from: string): string => {
    const match = from.match(/@([^>]+)/);
    return match ? match[1] : '';
  };

  // Debounced search to improve performance
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search emails from backend
  const searchEmails = useCallback(async (query: string, page: number = 1) => {
    if (!query.trim()) {
      // If search is cleared, fetch regular emails
      fetchEmails(1, true);
      return;
    }

    try {
      setIsSearching(true);
      setLoading(true);
      const offset = (page - 1) * pagination.limit;

      const response = await api.get<ApiResponse<Email[]>>('/emails/search', {
        params: {
          q: query,
          limit: pagination.limit,
          offset,
          ...(selectedEmployeeId && { employeeId: selectedEmployeeId })
        }
      });

      if (response.data.success && response.data.data) {
        if (page === 1) {
          setEmails(response.data.data);
        } else {
          setEmails(prev => [...prev, ...response.data.data]);
        }

        // Update pagination info
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            currentPage: page,
            totalPages: response.data.pagination!.totalPages,
            totalEmails: response.data.pagination!.total,
            hasMore: response.data.pagination!.hasMore
          }));
        }
      } else {
        if (page === 1) {
          setEmails([]);
        }
        console.error('Invalid search response format:', response.data);
      }
    } catch (error) {
      console.error('Error searching emails:', error);
      if (page === 1) {
        setEmails([]);
      }
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [pagination.limit, selectedEmployeeId, fetchEmails]);

  // Trigger search when debounced query changes
  useEffect(() => {
    if (isAuthenticated && currentView === 'inbox') {
      if (debouncedSearchQuery.trim()) {
        searchEmails(debouncedSearchQuery, 1);
      } else if (searchQuery === '' && debouncedSearchQuery === '') {
        // Only refetch when search is explicitly cleared
        setIsSearching(false);
        fetchEmails(1, true);
      }
    }
  }, [debouncedSearchQuery, isAuthenticated, currentView, searchEmails, fetchEmails, searchQuery]);

  // Group emails by thread
  const groupEmailsByThread = (emailList: Email[]): Thread[] => {
    const threadMap = new Map<string, {
      threadId: string;
      emails: Email[];
      latestEmail: Email;
      unreadCount: number;
      participants: Set<string>;
    }>();

    emailList.forEach(email => {
      if (!threadMap.has(email.threadId)) {
        threadMap.set(email.threadId, {
          threadId: email.threadId,
          emails: [],
          latestEmail: email,
          unreadCount: 0,
          participants: new Set<string>()
        });
      }

      const thread = threadMap.get(email.threadId)!;
      thread.emails.push(email);

      // Update latest email if this one is newer
      if (new Date(email.date) > new Date(thread.latestEmail.date)) {
        thread.latestEmail = email;
      }

      // Count unread emails
      if (!email.isRead) {
        thread.unreadCount++;
      }

      // Add participants
      thread.participants.add(email.from);
      thread.participants.add(email.to);
    });

    return Array.from(threadMap.values()).map(thread => ({
      ...thread,
      participants: Array.from(thread.participants),
      emailCount: thread.emails.length
    }));
  };

  // Group emails by thread (no client-side filtering needed anymore)
  const displayThreads = groupEmailsByThread(emails);



  // Handle tab switching
  const handleTabSwitch = async (view: ViewType) => {
    setTabLoading(true);
    setCurrentView(view);
    // Clear any existing intervals
    if (window.emailRefreshInterval) {
      clearInterval(window.emailRefreshInterval);
      window.emailRefreshInterval = null;
    }

    // Small delay to show loading state
    setTimeout(() => {
      setTabLoading(false);
    }, 100);
  };

  // Load data based on current view
  useEffect(() => {
    if (isAuthenticated) {
      console.log(`Loading data for view: ${currentView}`);
      if (currentView === 'inbox') {
        console.log('Fetching inbox data (employees + emails)');
        fetchEmployees(); // Fetch employees first
        fetchEmails(1, true); // Reset to first page
        // Refresh emails every 30 seconds only for inbox view
        window.emailRefreshInterval = setInterval(() => {
          console.log('Auto-refreshing inbox emails');
          //fetchEmails(1, true);
        }, 30000);
      } else if (currentView === 'sla') {
        console.log('SLA Monitoring view - no additional API calls needed');
      } else if (currentView === 'escalation') {
        console.log('Escalation Report view - no additional API calls needed');
      }
    }

    // Cleanup function
    return () => {
      if (window.emailRefreshInterval) {
        console.log('Clearing email refresh interval');
        clearInterval(window.emailRefreshInterval);
        window.emailRefreshInterval = null;
      }
    };
  }, [isAuthenticated, currentView, fetchEmails]);

  // Keyboard shortcut for search (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('.search-input') as HTMLInputElement | null;
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="app">
      {!isAuthenticated ? (
        <Login />
      ) : (
        <>
          <header className="header">
            <div className="header-content">
              <Mail className="header-icon" />
              <h1>Ops Email Inbox</h1>
              <div className="user-info">
                <span>Welcome, {user?.email}</span>
                <button onClick={handleLogout} className="logout-btn">
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </div>
          </header>

          <main className="main">
          <>
            {/* Navigation */}
            <nav className="main-navigation">
              <div className="nav-brand">
                <Mail size={24} />
                <span>OpsEmail</span>
              </div>
              <div className="nav-links">
                <button
                  className={`nav-link ${currentView === 'inbox' ? 'active' : ''}`}
                  onClick={() => handleTabSwitch('inbox')}
                >
                  <Mail size={16} />
                  Inbox
                </button>
                <button
                  className={`nav-link ai-chat-nav ${currentView === 'aichat' ? 'active' : ''}`}
                  onClick={() => handleTabSwitch('aichat')}
                >
                  <Sparkles size={16} />
                  AI Chat
                </button>
                {user?.role !== 'employee' && (
                  <button
                    className={`nav-link ${currentView === 'escalation' ? 'active' : ''}`}
                    onClick={() => handleTabSwitch('escalation')}
                  >
                    <BarChart3 size={16} />
                    Escalation Report
                  </button>
                )}
                {user?.role !== 'employee' && (
                  <button
                    className={`nav-link ${currentView === 'sla' ? 'active' : ''}`}
                    onClick={() => handleTabSwitch('sla')}
                  >
                    <Calendar size={16} />
                    SLA Monitoring
                  </button>
                )}
              </div>
            </nav>

            {tabLoading ? (
              <div className="simple-loading" style={{ padding: '2rem', textAlign: 'center' }}>
                Loading {currentView === 'inbox' ? 'Inbox' : currentView === 'aichat' ? 'AI Chat' : currentView === 'escalation' ? 'Escalation Report' : 'SLA Monitoring'}...
              </div>
            ) : currentView === 'inbox' ? (
          <div className="simple-inbox">
            {/* Simple Header */}
            <div className="simple-header">
              <h2>Email Inbox</h2>
              <div className="simple-controls">
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                  className="simple-select"
                  disabled={user?.role === 'employee'}
                >
                  {user?.role === 'employee' ? (
                    // Employee role: show only their own name
                    employees
                      .filter((emp: Employee) => String(emp.id) === selectedEmployeeId)
                      .map((emp: Employee) => (
                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                      ))
                  ) : (
                    // Admin/manager: show all employees
                    <>
                      <option value="">All Employees</option>
                      {employees.map((employee: Employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>

                <select
                  value={pendingFilter}
                  onChange={(e) => {
                    setPendingFilter(e.target.value);
                    fetchEmails(1, true);
                  }}
                  className="simple-select"
                >
                  <option value="employee">Pending with Employee</option>
                  <option value="client">Pending with Client</option>
                  <option value="">All Threads</option>
                </select>

                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Search entire inbox..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="simple-search"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="simple-btn"
                      style={{ padding: '4px 8px' }}
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <button
                  onClick={() => {
                    if (debouncedSearchQuery.trim()) {
                      searchEmails(debouncedSearchQuery, 1);
                    } else {
                      fetchEmails(1, true);
                    }
                  }}
                  className="simple-btn"
                >
                  Refresh
                </button>
              </div>
            </div>

            {/* Simple Two-Column Layout */}
            <div className="simple-layout">
              {/* Left Sidebar */}
              <div className="simple-sidebar">
                <h3>Threads ({displayThreads.length})</h3>

                {loading ? (
                  <div className="simple-loading">Loading...</div>
                ) : displayThreads.length === 0 ? (
                  <div className="simple-empty">No emails found</div>
                ) : (
                  <div className="simple-thread-list">
                    {displayThreads.map(thread => (
                      <div
                        key={thread.threadId}
                        className={`simple-thread ${selectedThread?.threadId === thread.threadId ? 'selected' : ''}`}
                        onClick={() => handleThreadSelect(thread)}
                      >
                        <div className="thread-title">
                          {thread.latestEmail?.subject || 'No Subject'}
                        </div>
                        <div className="thread-sender">
                          From: {getSenderName(thread.latestEmail?.from || 'Unknown')}
                        </div>
                        <div className="thread-date">
                          {thread.latestEmail?.date ? formatDate(thread.latestEmail.date) : 'Unknown Date'}
                        </div>
                        <div className="thread-count-row">
                          <span className="thread-count">
                            {thread.emailCount || 0} emails
                          </span>
                          <button
                            className="ai-draft-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAiDraftThread(thread);
                            }}
                            title="Generate AI Draft"
                          >
                            <Sparkles size={13} /> AI
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Pagination */}
                    {pagination.totalEmails > 0 && (
                      <div className="pagination">
                        <button
                          className="btn btn-secondary"
                          disabled={pagination.currentPage === 1}
                          onClick={() => {
                            if (debouncedSearchQuery.trim()) {
                              searchEmails(debouncedSearchQuery, pagination.currentPage - 1);
                            } else {
                              fetchEmails(pagination.currentPage - 1, true);
                            }
                          }}
                        >
                          <ChevronLeft className="btn-icon" />
                          Previous
                        </button>

                        <div className="pagination-info">
                          Page {pagination.currentPage} of {pagination.totalPages}
                          <span className="total-count">({pagination.totalEmails} total {debouncedSearchQuery.trim() ? 'search results' : 'emails'})</span>
                        </div>

                        <button
                          className="btn btn-secondary"
                          disabled={pagination.currentPage === pagination.totalPages}
                          onClick={() => {
                            if (debouncedSearchQuery.trim()) {
                              searchEmails(debouncedSearchQuery, pagination.currentPage + 1);
                            } else {
                              fetchEmails(pagination.currentPage + 1, false);
                            }
                          }}
                        >
                          Next
                          <ChevronRight className="btn-icon" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Content */}
              <div className="simple-content">
                {selectedThread ? (
                  <div className="email-view">
                    <div className="thread-header">
                      <h2>{selectedThread.latestEmail?.subject || 'No Subject'}</h2>
                      <div className="thread-meta">
                        <span className="thread-count-badge">
                          {selectedThread.emailCount || 0} emails in thread
                        </span>
                      </div>
                    </div>

                    {/* Thread Communication History */}
                    <div className="thread-communications">
                      {selectedThread.emails && selectedThread.emails.length > 0 ? (
                        selectedThread.emails
                          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) // Sort by date ascending (oldest first)
                          .map((email, index) => (
                            <div key={email._id || index} className={`email-message ${email.from?.includes('astrolabs.com') ? 'outgoing' : 'incoming'}`}>
                              <div className="email-message-header">
                                <div className="email-sender">
                                  <div className="sender-name">
                                    {getSenderName(email.from || 'Unknown')}
                                  </div>
                                  <div className="sender-email">
                                    {email.from || 'Unknown'}
                                  </div>
                                </div>
                                <div className="email-date">
                                  {email.date ? formatDate(email.date) : 'Unknown Date'}
                                </div>
                              </div>

                              <div className="email-message-content">
                                <div className="email-subject">
                                  {email.subject || 'No Subject'}
                                </div>
                                <div
                                  className="email-body-text"
                                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.body || email.snippet || 'No content available') }}
                                />
                              </div>

                              {email.attachments && email.attachments.length > 0 && (
                                <div className="email-attachments">
                                  <div className="attachments-label">Attachments:</div>
                                  <div className="attachment-list">
                                    {email.attachments.map((attachment, attIndex) => (
                                      <div key={attIndex} className="attachment-item">
                                        <Paperclip className="attachment-icon" size={14} />
                                        <span className="attachment-name">{attachment.filename || 'Unknown file'}</span>
                                        <span className="attachment-size">({attachment.size || 'Unknown size'} bytes)</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                      ) : (
                        <div className="no-emails-in-thread">
                          <p>No emails found in this thread</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="no-selection">
                    <p>Select a thread from the left to view email details</p>
                  </div>
                )}
              </div>
            </div>
          </div>
                ) : currentView === 'aichat' ? (
          <AIChatPanel />
                ) : currentView === 'escalation' ? (
          <EscalationReport />
        ) : (
          <SLAMonitoringDashboard user={user} />
        )}
          </>
          </main>
        </>
      )}

      {/* AI Draft Panel Modal */}
      {aiDraftThread && (
        <AIDraftPanel
          thread={aiDraftThread}
          onClose={() => setAiDraftThread(null)}
        />
      )}
    </div>
  );
}

export default App;
